import { Spin } from "antd";
import i18n from "i18next";
import type { FC } from "react";
import { useCallback, useMemo, useState } from "react";

import { FormManager } from "../../../../../components/Form/FormManager";
import { useFormSubmitCompletion } from "../../../../../components/Form/hooks/useFormSubmitCompletion";
import { useLoadFormDefinitionQuery } from "../../../../../components/Form/services/server/serverApi";
import { FormLayoutType } from "../../../../../components/Form/types";
import type { FormProps } from "../../../../../components/Form/types/FormProps";
import { ResourceName, ResourceVariant } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../core/router/types/routerTypes";
import { mergeHostTerminalIdMaskFields } from "../../services/mappers/mergeHostTerminalIdMaskFields";
import { useAcquirerSaveMutation } from "../../services/server/AcquirerApi";
import type { AcquirerEntity } from "../../types/AcquirerEntity";

export const AcquirerCreateForm: FC<FormProps> = ({ runSubmit, formAction }) => {
    const { data: formDefinition, isFetching: configIsFetching } = useLoadFormDefinitionQuery("acquirer-physical");

    const transformedFormConfig = useMemo(
        () => mergeHostTerminalIdMaskFields(formDefinition?.formConfiguration),
        [formDefinition?.formConfiguration],
    );

    const [formData, setFormData] = useState<AcquirerEntity>({ physical: true } as AcquirerEntity);
    const onChangeCallback = useCallback(setFormData, [setFormData]);

    const completeSubmit = useFormSubmitCompletion(formAction, `${BASE_PATH}/${ResourceName.ACQUIRER}`);
    const [saveFormData, { isLoading: saveFormIsInProgress }] = useAcquirerSaveMutation();
    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData) {
                return;
            }

            const result = await saveFormData({ data: formData });

            const hasError = "error" in result;
            if (!hasError) {
                completeSubmit(
                    i18n.t("common.formSaveSuccess", {
                        id: result.data.id,
                        resource: "Acquirer",
                    }),
                    `${BASE_PATH}/${ResourceName.ACQUIRER}/${ResourceVariant.PHYSICAL}/${FORM_ACTION.EDIT}/${result.data.id}?acquirerId=${result.data.id}`,
                );
            }
        },
        [saveFormData, formData, completeSubmit],
    );

    return (
        <Spin spinning={saveFormIsInProgress || configIsFetching}>
            {transformedFormConfig && (
                <FormManager<AcquirerEntity>
                    name="AcquirerPhysicalCreateForm"
                    formData={formData}
                    formLayout={FormLayoutType.TABS}
                    formDefinition={transformedFormConfig}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
